/**
 * FR-018.7 — the Sources ("Herkunft") section of every object view
 * (TENANT_CONFIGURATION_UI.md §2, response shape §6.1).
 *
 * Order: master/origin, confirmed (open ranges before closed), shared, suggested,
 * unassigned, merge-suggested, then alias spellings collapsed after three. The
 * panel only reads and fires intents; the editor and group panels are hosted by
 * the page (`onOpenEditor` / `onOpenGroup`).
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { LenseConfig } from '../../../../services/twinService';
import { sourcesErrorKind, type SourceRowDto, type SourcesConfig } from '../../../../services/sourcesService';
import { useEntitySources, useSourceDuplicates, useSourceMerge, useSourceReject } from '../../../../hooks/useSources';
import { sourcesConfigFor } from './sourcesConfig';
import { SourceMasterRow, SourceRow, type SourceEditorMode } from './SourceRow';
import { isClosedRange } from './validity';
import { errorKindSentence } from './usePreviewConfirm';
import './SourcesPanel.css';

/** What a row (or the footer) asks the editor to do. */
export type RowEditorIntent = { mode: SourceEditorMode; row?: SourceRowDto };
/** A row intent stamped with the object it was opened on, so the editor can refuse a stale one. */
export type EditorIntent = RowEditorIntent & {
    entityType: string;
    entityId: string;
    /** `assign` from the needs-assignment card: the target already chosen there (prefills the editor). */
    target?: { entity_id: string; name: string; properties?: { area_ha?: number; crop?: string } };
    /** `assign` from the needs-assignment card: the chosen `gilt ab Erntejahr` (prefills `von`). */
    validFrom?: number;
};

export interface SourcesPanelProps {
    entityType: string;
    entityId: string;
    lens?: LenseConfig;
    /** Fetch only when true (the object view's live mode). */
    enabled?: boolean;
    onOpenEditor(intent: EditorIntent): void;
    onOpenGroup(row: SourceRowDto): void;
    onNavigate(entityType: string, entityId: string): void;
    onPreviewDocument?(docId: string): void;
    /** The page's own title for the object: names an object whose API name is only its id. */
    displayName?: string;
}

const ALIAS_VISIBLE = 3;
const KIND_ORDER = ['confirmed', 'shared', 'suggested', 'unassigned', 'merge_suggested'] as const;

type T = (key: string, def: string, opts?: Record<string, unknown>) => string;

/** Rejected duplicates survive navigation: per object, in this browser only. */
export const dismissedDuplicatesKey = (entityType: string, entityId: string) =>
    `hofsync.sources.dismissedDuplicates:${entityType}:${entityId}`;

function readDismissed(key: string): Set<string> {
    try {
        const raw = window.localStorage.getItem(key);
        const parsed: unknown = raw ? JSON.parse(raw) : [];
        return new Set(Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : []);
    } catch {
        return new Set();
    }
}

function writeDismissed(key: string, ids: ReadonlySet<string>): void {
    try {
        window.localStorage.setItem(key, JSON.stringify([...ids]));
    } catch {
        // Private mode / blocked storage: the in-memory set still hides the row for this view.
    }
}

export const SourcesPanel: React.FC<SourcesPanelProps> = ({
    entityType,
    entityId,
    lens,
    enabled = true,
    onOpenEditor,
    onOpenGroup,
    onPreviewDocument,
    displayName,
}) => {
    const { t: rawT } = useTranslation();
    const t = rawT as unknown as T;
    const { data, isLoading, error } = useEntitySources(entityType, entityId, enabled);
    const fallbackConfig = sourcesConfigFor(lens);
    // The API's own `config` wins; the lens vocabulary covers the loading/error states.
    const config: SourcesConfig = data?.config ?? { ...fallbackConfig, master_role: fallbackConfig.master_role ?? null };
    const { data: duplicates } = useSourceDuplicates(
        entityType,
        entityId,
        enabled && !!data && config.unassigned_verb === 'merge',
    );
    const reject = useSourceReject();
    const merge = useSourceMerge();
    const [showAllAliases, setShowAllAliases] = React.useState(false);
    // Duplicates have no mapping row to reject server-side; "Ablehnen" hides them, persisted per object.
    const storageKey = dismissedDuplicatesKey(entityType, entityId);
    const [dismissed, setDismissed] = React.useState<ReadonlySet<string>>(() => readDismissed(storageKey));
    const [merged, setMerged] = React.useState<ReadonlySet<string>>(() => new Set());
    // The last failed action per row (mapping id / duplicate id), cleared on the next attempt.
    const [rowErrors, setRowErrors] = React.useState<Readonly<Record<string, string>>>({});
    const errorSentence = (err: unknown) =>
        errorKindSentence(t, sourcesErrorKind(err), 'farm.sources.errors.sources_unavailable', 'Die Herkunftsangaben sind gerade nicht verfügbar.');
    const setRowError = (id: string, message: string | null) =>
        setRowErrors((prev) => {
            if (message === null) {
                if (!(id in prev)) return prev;
                const next = { ...prev };
                delete next[id];
                return next;
            }
            return { ...prev, [id]: message };
        });
    const dismissDuplicate = (id: string) =>
        setDismissed((prev) => {
            const next = new Set(prev).add(id);
            writeDismissed(storageKey, next);
            return next;
        });

    const openEditor = (intent: RowEditorIntent) => onOpenEditor({ ...intent, entityType, entityId });

    // No registry kind targets the type (e.g. a crop cycle): there is nothing a reference could map to.
    const footer = config.addable === false ? null : (
        <div className="sources-panel__footer">
            <button type="button" className="sources-panel__add" onClick={() => openEditor({ mode: 'add' })}>
                {t('farm.sources.actions.addReference', 'Referenz hinzufügen')}
            </button>
        </div>
    );

    if (isLoading) {
        return (
            <div className="sources-panel sources-panel--loading" aria-busy="true">
                <div className="sources-panel__skeleton" data-testid="sources-skeleton-row" />
                <div className="sources-panel__skeleton" data-testid="sources-skeleton-row" />
            </div>
        );
    }
    if (error || !data) {
        if (!error && !enabled) return null;
        const kind = sourcesErrorKind(error) ?? 'sources_unavailable';
        return (
            <div className="sources-panel">
                <p className="sources-panel__error">
                    {t(`farm.sources.errors.${kind}`, t('farm.sources.errors.sources_unavailable', 'Die Herkunftsangaben sind gerade nicht verfügbar.'))}
                </p>
            </div>
        );
    }

    const year = new Date().getFullYear();
    const byKind = (kind: string) => data.rows.filter((r) => r.kind === kind);
    const confirmed = byKind('confirmed');
    const ordered: SourceRowDto[] = [
        ...confirmed.filter((r) => !isClosedRange(r, year)),
        ...confirmed.filter((r) => isClosedRange(r, year)),
        ...KIND_ORDER.slice(1).flatMap((k) => byKind(k)),
    ];
    const duplicateRows = (duplicates ?? [])
        .filter((d) => !dismissed.has(d.entity_id) && !merged.has(d.entity_id))
        .map((d) => ({
            id: d.entity_id,
            row: {
                kind: 'merge_suggested',
                mapping_id: null,
                alias_id: null,
                source_system: null,
                source_label: d.name,
                relation: null,
                registry_kind: null,
                valid_from: null,
                valid_to: null,
                validity_kind: 'global',
                status: 'proposed',
                origin: null,
                note: null,
                evidence: d.evidence_key,
                counts: {},
            } satisfies SourceRowDto,
        }));
    const aliases = byKind('alias');
    const visibleAliases = showAllAliases ? aliases : aliases.slice(0, ALIAS_VISIBLE);
    const hiddenAliases = aliases.slice(ALIAS_VISIBLE);
    const hiddenDocs = hiddenAliases.reduce<number | null>(
        (sum, r) => (typeof r.counts?.documents === 'number' ? (sum ?? 0) + r.counts.documents : sum),
        null,
    );

    const handleReject = (row: SourceRowDto) => {
        const id = row.mapping_id;
        if (!id) return;
        setRowError(id, null);
        reject.mutate({ mapping_id: id }, { onError: (err) => setRowError(id, errorSentence(err)) });
    };
    const rowProps = {
        config,
        onOpenEditor: openEditor,
        onOpenGroup,
        onReject: handleReject,
    };

    return (
        <div className="sources-panel">
            <SourceMasterRow
                master={data.master}
                objectName={
                    displayName && (!data.object.name || data.object.name === data.object.entity_id)
                        ? displayName
                        : data.object.name
                }
                config={config}
                onPreviewDocument={onPreviewDocument}
            />
            {ordered.map((row, i) => (
                <SourceRow
                    key={row.mapping_id ?? row.alias_id ?? `${row.kind}-${i}`}
                    row={row}
                    dimmed={isClosedRange(row, year)}
                    firstSeen={row.kind === 'unassigned' ? data.object.stub?.first_seen : undefined}
                    busy={reject.isPending}
                    error={row.mapping_id ? rowErrors[row.mapping_id] : null}
                    {...rowProps}
                />
            ))}
            {duplicateRows.map(({ id, row }) => (
                <SourceRow
                    key={`dup-${id}`}
                    row={row}
                    mergeCandidateId={id}
                    busy={merge.isPending}
                    error={rowErrors[`dup:${id}`]}
                    {...rowProps}
                    onReject={() => dismissDuplicate(id)}
                    onMerge={(loserId, done) => {
                        setRowError(`dup:${loserId}`, null);
                        merge.mutate(
                            { entity_type: entityType, loser_id: loserId, winner_id: entityId },
                            {
                                onSuccess: () => {
                                    done();
                                    setMerged((prev) => new Set(prev).add(loserId));
                                },
                                onError: (err) => setRowError(`dup:${loserId}`, errorSentence(err)),
                            },
                        );
                    }}
                />
            ))}
            {visibleAliases.map((row, i) => (
                <SourceRow key={row.alias_id ?? `alias-${i}`} row={row} {...rowProps} />
            ))}
            {hiddenAliases.length > 0 && (
                <button
                    type="button"
                    className="sources-panel__toggle"
                    aria-expanded={showAllAliases}
                    onClick={() => setShowAllAliases((v) => !v)}
                >
                    {showAllAliases
                        ? t('farm.sources.aliases.showLess', 'weniger anzeigen')
                        : hiddenDocs != null
                          ? t('farm.sources.aliases.more', '{{n}} weitere Schreibweisen · {{docs}} · alle anzeigen', {
                                count: hiddenAliases.length,
                                n: hiddenAliases.length,
                                docs: t('farm.sources.counts.documents', '{{n}} Belege', { count: hiddenDocs, n: hiddenDocs }),
                            })
                          : t('farm.sources.aliases.moreNoDocs', '{{n}} weitere Schreibweisen · alle anzeigen', {
                                count: hiddenAliases.length,
                                n: hiddenAliases.length,
                            })}
                </button>
            )}
            {footer}
        </div>
    );
};

export default SourcesPanel;
